import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Card,
  CardContent,
  MenuItem,
} from "@mui/material";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew"; // Icon for enable/disable
import NotificationSnackbar from "./NotificationBar"; // Ensure this path matches your project structure

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function EmployeeRBMContracts() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const DEFAULT_COLOR = "#FFFFFF";

  const [searchTerm, setSearchTerm] = useState("");
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showData, setShowData] = useState(false);

  // Menu States
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const actionMenuOpen = Boolean(anchorEl);

  // View Modal States
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [contractToView, setContractToView] = useState(null);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setShowData(false);
      setContracts([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        `/api/support/employee-rbm-contract?employeeId=${term}`,
      );
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg = (data && data.message) || `HTTP ${res.status}`;
        setError(msg);
        setShowData(true);
        setContracts([]);
        return;
      }

      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        // Sort by fiscalYearId descending (latest year first)
        const sortedList = list.sort((a, b) => {
          const yearA = a.fiscalYearId || "";
          const yearB = b.fiscalYearId || "";
          return yearB.localeCompare(yearA);
        });

        setContracts(sortedList);
        setShowData(true);
        setError(null);
      } else {
        setContracts([]);
        setShowData(true);
        setError("No contracts found for this Employee ID.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setError("Search failed. Please try again.");
      setShowData(true);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setError(null);
    setShowData(false);
    setContracts([]);
  };

  const handleMenuOpen = (event, contract) => {
    setAnchorEl(event.currentTarget);
    setSelectedContract(contract);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContract(null);
  };

  const handleActionViewDetails = () => {
    if (selectedContract) {
      setContractToView(selectedContract);
      setViewModalOpen(true);
    }
    handleMenuClose();
  };

  const handleToggleContractStatus = async (contractId, currentStatus) => {
    try {
      const response = await fetch("/api/support/change-rbm-contract-status", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: contractId,
          isActive: !currentStatus,
        }),
      });

      if (response.ok) {
        const updatedContract = await response.json();
        setSnackbarMessage(
          `Contract ${updatedContract.isActive ? "enabled" : "disabled"} successfully!`,
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        // Update the local state to reflect the change
        setContracts((prevContracts) =>
          prevContracts.map((c) => (c.id === contractId ? updatedContract : c)),
        );
        // If the currently viewed contract is the one updated, update its state too
        if (contractToView && contractToView.id === contractId) {
          setContractToView(updatedContract);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSnackbarMessage(
          errorData.message || "Failed to update contract status.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating contract status:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const parts = fullName.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const DetailField = ({ label, value, icon: Icon }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
      {Icon && (
        <Box
          sx={{
            mr: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: alpha(SECONDARY_COLOR, 0.08),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            display: "block",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: PRIMARY_COLOR,
            wordWrap: "break-word",
            fontSize: "0.95rem",
          }}
        >
          {value !== undefined && value !== null ? value : "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          RBM Contracts Management
        </Typography>
      </Box>

      {/* SEARCH CARD */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search Employee RBM Contracts
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Enter an Employee ID to view all of their historical Results-Based
          Management (RBM) contracts.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Enter Employee ID"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (!v.trim()) setShowData(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Collapse in={showData} timeout="auto">
          {error ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          ) : contracts && contracts.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {contracts.length} Contract
                {contracts.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Employee ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Employee Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Fiscal Year
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Contract Type
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Activities
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Score
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Institution
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contracts.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {c.employeeId ?? "-"}
                        </TableCell>
                        <TableCell>{c.employee || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={`FY ${c.fiscalYearId}`}
                            size="small"
                            sx={{
                              bgcolor: alpha(SECONDARY_COLOR, 0.1),
                              color: SECONDARY_COLOR,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>{c.contractType || "-"}</TableCell>
                        <TableCell align="center">
                          {c.numOfActivities ?? 0}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color:
                                c.totalScore >= 70
                                  ? "success.main"
                                  : "warning.main",
                            }}
                          >
                            {c.totalScore ?? 0}%
                          </Typography>
                        </TableCell>
                        <TableCell>{c.Institution || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={c.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={c.isActive ? "success" : "default"}
                            sx={{
                              fontWeight: 600,
                              borderRadius: 2,
                              ...(c.isActive
                                ? {}
                                : {
                                    bgcolor: alpha(
                                      theme.palette.grey[500],
                                      0.15,
                                    ),
                                    color: theme.palette.grey[700],
                                  }),
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, c)}
                            size="small"
                            sx={{
                              color: PRIMARY_COLOR,
                              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                              "&:hover": {
                                backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>

              <Menu
                anchorEl={anchorEl}
                open={actionMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                    mt: 1,
                    minWidth: 180,
                    borderRadius: 2,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: alpha(PRIMARY_COLOR, 0.04),
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleActionViewDetails}>
                  <ListItemIcon>
                    <VisibilityIcon
                      fontSize="small"
                      sx={{ color: SECONDARY_COLOR }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="View Details"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>

      {/* VIEW DETAILS MODAL */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            backgroundColor: "#f8fafc",
          },
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: "#ffffff",
            borderBottom: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: PRIMARY_COLOR }}
          >
            RBM Contract Details
          </Typography>
          <IconButton
            onClick={() => setViewModalOpen(false)}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: PRIMARY_COLOR,
                bgcolor: alpha(PRIMARY_COLOR, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
          {contractToView && (
            <Box>
              {/* Profile Header Widget */}
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  p: 3,
                  mb: 4,
                  borderRadius: 4,
                  border: `1px solid ${alpha(SECONDARY_COLOR, 0.15)}`,
                  background: `linear-gradient(135deg, ${DEFAULT_COLOR} 0%, ${alpha(SECONDARY_COLOR, 0.05)} 100%)`,
                }}
              >
                <Avatar
                  sx={{
                    width: 86,
                    height: 86,
                    bgcolor: PRIMARY_COLOR,
                    fontSize: "2.2rem",
                    fontWeight: 700,
                    mb: { xs: 2, sm: 0 },
                    mr: { sm: 3 },
                    boxShadow: `0 4px 14px ${alpha(PRIMARY_COLOR, 0.3)}`,
                  }}
                >
                  {getInitials(contractToView.employee)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: PRIMARY_COLOR, mb: 0.5 }}
                  >
                    {contractToView.employee || "Unknown Employee"}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: SECONDARY_COLOR, fontWeight: 600, mb: 1.5 }}
                  >
                    {contractToView.Institution || "Institution Not Specified"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      icon={<BusinessCenterIcon fontSize="small" />}
                      label={contractToView.contractType}
                      color="primary"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                    <Chip
                      label={`FY: ${contractToView.fiscalYearId}`}
                      variant="outlined"
                      color="secondary"
                      sx={{
                        fontWeight: 600,
                        borderRadius: 2,
                        bgcolor: "white",
                      }}
                    />
                    {/* Display Status Chip */}
                    <Chip
                      label={
                        contractToView.isActive
                          ? "Active Contract"
                          : "Inactive Contract"
                      }
                      color={contractToView.isActive ? "success" : "default"}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        borderRadius: 2,
                        ...(contractToView.isActive
                          ? {}
                          : {
                              bgcolor: alpha(theme.palette.grey[500], 0.15),
                              color: theme.palette.grey[700],
                            }),
                      }}
                    />
                    {/* Enable/Disable Button within Dialog Actions or Body */}

                    <Button
                      variant="contained"
                      startIcon={<PowerSettingsNewIcon />}
                      onClick={() =>
                        handleToggleContractStatus(
                          contractToView.id,
                          contractToView.isActive,
                        )
                      }
                      color={contractToView.isActive ? "error" : "success"}
                      sx={{
                        fontWeight: 400,
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        minWidth: "100px", // Ensure consistent button width
                        textTransform: "none",
                        "&.Mui-disabled": {
                          // Style for disabled state if needed
                          backgroundColor:
                            theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        },
                      }}
                    >
                      {contractToView.isActive
                        ? "Disable Contract"
                        : "Enable Contract"}
                    </Button>
                  </Box>
                </Box>
              </Paper>

              <Grid container spacing={4}>
                {/* General Information Card */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <PersonOutlineIcon
                          sx={{ color: SECONDARY_COLOR, mr: 1 }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
                        >
                          General Contract Info
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }}
                      />

                      <DetailField
                        icon={BadgeIcon}
                        label="Contract ID"
                        value={contractToView.id}
                      />
                      <DetailField
                        icon={BadgeIcon}
                        label="Employee ID"
                        value={contractToView.employeeId}
                      />
                      <DetailField
                        icon={CorporateFareIcon}
                        label="Institution"
                        value={contractToView.Institution}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Start Date"
                        value={formatDate(contractToView.startOn)}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="End Date"
                        value={formatDate(contractToView.endOn)}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Created On"
                        value={formatDate(contractToView.createdOn)}
                      />
                      <DetailField
                        icon={AccountCircleIcon}
                        label="Created By"
                        value={contractToView.createdBy}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Performance Metrics Card */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 3 }}
                      >
                        <AssessmentIcon
                          sx={{ color: SECONDARY_COLOR, mr: 1 }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}
                        >
                          Activities & Performance Scoring
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }}
                      />

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Total Activities"
                            value={contractToView.numOfActivities}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Total Weight"
                            value={contractToView.totalWeight}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="On Track"
                            value={contractToView.ontrackActivities}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Due Activities"
                            value={contractToView.dueActivities}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Reported"
                            value={contractToView.reportedActivities}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Scored Activities"
                            value={contractToView.scoredActivities}
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Target vs Achieved"
                            value={`${contractToView.target} / ${contractToView.achievedTarget}`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Rejected"
                            value={contractToView.rejectedActivities}
                          />
                        </Grid>
                      </Grid>

                      <Divider
                        sx={{ my: 2, borderColor: alpha(PRIMARY_COLOR, 0.05) }}
                      />

                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(SECONDARY_COLOR, 0.05),
                          border: `1px dashed ${alpha(SECONDARY_COLOR, 0.3)}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          Scores Outline
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Contract Score
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: PRIMARY_COLOR, fontWeight: 800 }}
                            >
                              {contractToView.score}%
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Competency
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: PRIMARY_COLOR, fontWeight: 800 }}
                            >
                              {contractToView.competencyScore}%
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color="text.secondary">
                              Total Score
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                color: theme.palette.success.main,
                                fontWeight: 800,
                              }}
                            >
                              {contractToView.totalScore}%
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: "#ffffff" }}>
          <Button
            onClick={() => setViewModalOpen(false)}
            variant="contained"
            disableElevation
            sx={{
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: 2,
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: SECONDARY_COLOR },
            }}
          >
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default EmployeeRBMContracts;
